import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import tidalLogo from '../assets/tidal-logo-tight.png';
import { inventoryApi } from '../api/inventory-api-instance';
import { NotFoundError } from '../api/errors';
import type { Item } from '../domain/inventory';
import { useAuth } from '../auth/AuthContext';
import { authErrorMessage, cleanAuthReturnTo, resolveAuthGate } from '../auth/auth-navigation';
import {
  computeInventorySummary,
  FULL_FLEET_ITEMS_QUERY_KEY,
  FULL_FLEET_PAGE_SIZE,
} from '../features/dashboard/aggregations';
import { operationallyVisibleItems } from '../features/inventory/operational-visibility';
import { matchSites } from '../features/site-search/site-search';
import { toMap } from '../lib/collections';
import { resolveDocumentTitle } from './page-title';

type Theme = 'light' | 'dark';

function readInitialTheme(): Theme {
  const saved = window.localStorage.getItem('leatherback-theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readInitialSidebar(): boolean {
  const saved = window.localStorage.getItem('tidal-inventory-sidebar');
  if (saved === 'open' || saved === 'closed') return saved === 'open';
  return window.matchMedia('(min-width: 901px)').matches;
}

function accountInitials(name: string | undefined, email: string): string {
  const source = name?.trim() || email.split('@')[0] || email;
  const words = source.split(/[\s._-]+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
}

function readItemIds(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[\s,;]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  ];
}

export function AppShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const signInStartedRef = useRef(false);
  const isLive = import.meta.env.VITE_API_MODE === 'live';
  const [theme, setTheme] = useState<Theme>(readInitialTheme);
  const [sidebarOpen, setSidebarOpen] = useState(readInitialSidebar);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchPending, setSearchPending] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // -1 = no site highlighted, so Enter keeps the item ID lookup.
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const {
    configured,
    user,
    isLoading: authLoading,
    hasError: authErrorLoading,
    signInUrl,
    signOut,
  } = useAuth();
  const authError = isLive ? new URLSearchParams(location.search).get('authError') : null;
  const isExceptionRoute =
    location.pathname === '/inventory' && new URLSearchParams(location.search).has('attention');
  const returnTo = cleanAuthReturnTo(location.pathname, location.search, location.hash);
  const authGate = resolveAuthGate({
    isLive,
    isLoading: authLoading,
    hasError: authErrorLoading,
    configured,
    hasUser: Boolean(user),
    authError,
  });

  // Same query key as Dashboard/Sites/Parts' own full-fleet fetch, so they all
  // share one cached request instead of each fetching the entire item list
  // separately — this query is mounted for the whole app session, so a
  // second independent fetch here would double the cost of every page load.
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
    enabled: !isLive || (!authLoading && (!configured || Boolean(user))),
  });
  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: () => inventoryApi.listSites(),
    enabled: !isLive || (!authLoading && (!configured || Boolean(user))),
  });
  const sitesById = useMemo(() => toMap(sites.data), [sites.data]);
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
    enabled: !isLive || (!authLoading && (!configured || Boolean(user))),
  });
  // Site suggestions for one term only: several pasted IDs are an item batch.
  const siteSuggestions = useMemo(
    () =>
      readItemIds(searchText).length > 1
        ? []
        : matchSites(searchText, sites.data ?? [], customers.data ?? []),
    [customers.data, searchText, sites.data],
  );
  const showSuggestions = suggestionsOpen && siteSuggestions.length > 0;
  const summary = useMemo(
    () => computeInventorySummary(operationallyVisibleItems(items.data?.data ?? [], sitesById)),
    [items.data, sitesById],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem('leatherback-theme', theme);
  }, [theme]);

  useEffect(() => {
    window.localStorage.setItem('tidal-inventory-sidebar', sidebarOpen ? 'open' : 'closed');
  }, [sidebarOpen]);

  useEffect(() => {
    document.title = resolveDocumentTitle({
      pathname: location.pathname,
      search: location.search,
      authGate,
      sessionUnavailable: authErrorLoading,
    });
  }, [authErrorLoading, authGate, location.pathname, location.search]);

  useEffect(() => {
    if (!window.matchMedia('(min-width: 901px)').matches) setSidebarOpen(false);
    setAccountOpen(false);
    setSuggestionsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!accountOpen) return;

    function closeAccountMenu(event: PointerEvent): void {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    }

    function closeAccountMenuWithKeyboard(event: KeyboardEvent): void {
      if (event.key === 'Escape') setAccountOpen(false);
    }

    window.addEventListener('pointerdown', closeAccountMenu);
    window.addEventListener('keydown', closeAccountMenuWithKeyboard);
    return () => {
      window.removeEventListener('pointerdown', closeAccountMenu);
      window.removeEventListener('keydown', closeAccountMenuWithKeyboard);
    };
  }, [accountOpen]);

  useEffect(() => {
    if (authGate !== 'redirect') {
      signInStartedRef.current = false;
      return;
    }
    if (signInStartedRef.current) return;
    signInStartedRef.current = true;
    window.location.replace(signInUrl(returnTo));
  }, [authGate, returnTo, signInUrl]);

  useEffect(() => {
    if (user && authError) void navigate(returnTo, { replace: true });
  }, [authError, navigate, returnTo, user]);

  // The real backend has no full-text search endpoint yet — only an exact
  // item ID lookup (GET /v1/items/:id). A single term still tries that exact
  // lookup first (fastest path for the common case of pasting a full serial),
  // then falls back to a client-side prefix match against the full fleet (see
  // FULL_FLEET_ITEMS_QUERY_KEY above) so a partial serial like "VVPB2611500"
  // finds every item whose ID starts with it. ensureQueryData (rather than
  // reading the `items` query result directly) guarantees that fleet is
  // actually loaded before matching against it -- otherwise a search fired
  // before the background fetch resolves would silently see zero items and
  // report a false "not found". A comma- or whitespace-delimited list of
  // terms reuses Inventory's exact ID filter so logistics can prepare a batch
  // change without fuzzy matches.
  function openSiteSuggestion(index: number): void {
    const suggestion = siteSuggestions[index];
    if (!suggestion) return;
    setSearchText('');
    setSuggestionsOpen(false);
    setActiveSuggestion(-1);
    searchRef.current?.blur();
    void navigate(suggestion.path);
  }

  function handleSearchKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setSuggestionsOpen(false);
      setActiveSuggestion(-1);
      return;
    }
    if (siteSuggestions.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setSuggestionsOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // Wraps through -1 (the typed text) so the item lookup is reachable again.
      setActiveSuggestion((current) => {
        const next = current + step;
        if (next < -1) return siteSuggestions.length - 1;
        if (next >= siteSuggestions.length) return -1;
        return next;
      });
      return;
    }
    if (event.key === 'Enter' && showSuggestions && activeSuggestion >= 0) {
      event.preventDefault();
      openSiteSuggestion(activeSuggestion);
    }
  }

  async function handleSearchSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSuggestionsOpen(false);
    const value = new FormData(event.currentTarget).get('q');
    const ids = typeof value === 'string' ? readItemIds(value) : [];
    if (ids.length === 0) return;
    setSearchError(null);
    if (ids.length > 1) {
      const params = new URLSearchParams({ itemIds: ids.join(',') });
      void navigate(`/inventory?${params.toString()}`);
      return;
    }

    const [id] = ids;
    if (!id) return;
    setSearchPending(true);
    try {
      const item = await inventoryApi.getItem(id);
      void navigate(`/inventory/${encodeURIComponent(item.id)}`);
    } catch (error) {
      if (!(error instanceof NotFoundError)) {
        setSearchError('Search failed. Try again.');
        return;
      }
      let fleet: Item[];
      try {
        const page = await queryClient.ensureQueryData({
          queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
          queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
        });
        fleet = page.data;
      } catch {
        setSearchError(`No item found with ID "${id}"`);
        return;
      }
      const matches = fleet.filter((candidate) => candidate.id.startsWith(id));
      if (matches.length === 1) {
        void navigate(`/inventory/${encodeURIComponent(matches[0]!.id)}`);
        return;
      }
      if (matches.length > 1) {
        const params = new URLSearchParams({
          itemIds: matches.map((match) => match.id).join(','),
        });
        void navigate(`/inventory?${params.toString()}`);
        return;
      }
      setSearchError(`No item found with ID "${id}"`);
    } finally {
      setSearchPending(false);
    }
  }

  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  function toggleTheme(): void {
    setTheme(nextTheme);
  }

  if (authGate === 'pending' || authGate === 'redirect') {
    return (
      <div className="auth-gateway" role="status" aria-label="Redirecting to Google sign-in">
        <div className="auth-progress" aria-hidden="true">
          <span />
        </div>
        <img className="auth-brand-mark" src="/favicon.png" alt="" width="44" height="44" />
        <span className="visually-hidden">Redirecting to Google sign-in</span>
      </div>
    );
  }

  if (authGate === 'recovery') {
    const sessionUnavailable = authErrorLoading;
    return (
      <main className="auth-gateway">
        <section className="auth-recovery" aria-labelledby="auth-recovery-title">
          <div className="auth-recovery-brand">
            <img className="auth-brand-mark" src="/favicon.png" alt="" width="28" height="28" />
            <span>Tidal Inventory</span>
          </div>
          <div className="auth-recovery-message">
            <h1 id="auth-recovery-title">
              {sessionUnavailable ? 'Session unavailable' : "Sign-in couldn't be completed"}
            </h1>
            <p>
              {sessionUnavailable
                ? "We couldn't verify this session. Reconnect to continue."
                : authErrorMessage(authError)}
            </p>
          </div>
          <div className="auth-recovery-actions">
            {sessionUnavailable ? (
              <button type="button" onClick={() => window.location.reload()}>
                Retry session
              </button>
            ) : (
              <a className="primary-link" href={signInUrl(returnTo)}>
                Try Google sign-in again
              </a>
            )}
          </div>
        </section>
      </main>
    );
  }

  return (
    <div className={`app-shell app-shell-sidebar-${sidebarOpen ? 'open' : 'closed'}`}>
      <div className="brand-cell">
        <Link className="brand-link" to="/" aria-label="Go to dashboard">
          <img className="brand-logo brand-logo-full" src={tidalLogo} alt="Tidal" height={22} />
          <img className="brand-logo brand-logo-mark" src="/favicon.png" alt="" height={28} />
          <span className="brand-sublabel">Inventory</span>
        </Link>
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setSidebarOpen((open) => !open)}
          aria-label={`${sidebarOpen ? 'Hide' : 'Show'} navigation`}
          aria-expanded={sidebarOpen}
          title={`${sidebarOpen ? 'Hide' : 'Show'} navigation`}
        >
          <span className="sidebar-toggle-icon" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </button>
      </div>
      <header className="topbar">
        <div className="topbar-main">
          <form
            className="topbar-search"
            role="search"
            onSubmit={(event) => void handleSearchSubmit(event)}
          >
            <input
              ref={searchRef}
              name="q"
              type="search"
              placeholder="Search sites or item IDs"
              aria-label="Search sites or item IDs"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showSuggestions}
              aria-controls="topbar-site-suggestions"
              aria-activedescendant={
                showSuggestions && activeSuggestion >= 0
                  ? `topbar-site-suggestion-${activeSuggestion}`
                  : undefined
              }
              autoComplete="off"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setSuggestionsOpen(true);
                setActiveSuggestion(-1);
                setSearchError(null);
              }}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => setSuggestionsOpen(false)}
              onKeyDown={handleSearchKeyDown}
            />
            <kbd>⌘K</kbd>
            <button type="submit" disabled={searchPending}>
              {searchPending ? 'Looking up…' : 'Go'}
            </button>
            {showSuggestions ? (
              <ul
                id="topbar-site-suggestions"
                className="topbar-search-suggestions"
                role="listbox"
                aria-label="Sites"
              >
                {siteSuggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.site.id}
                    id={`topbar-site-suggestion-${index}`}
                    role="option"
                    aria-selected={index === activeSuggestion}
                    className={index === activeSuggestion ? 'is-active' : undefined}
                    // mousedown, not click: the input's blur would close the list first.
                    onMouseDown={(event) => {
                      event.preventDefault();
                      openSiteSuggestion(index);
                    }}
                    onMouseEnter={() => setActiveSuggestion(index)}
                  >
                    <strong>{suggestion.site.name}</strong>
                    <span>{suggestion.site.displayName ?? ''}</span>
                    <small>
                      {[suggestion.customerName, suggestion.site.region]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </li>
                ))}
              </ul>
            ) : null}
            {searchError ? (
              <span className="topbar-search-error" role="alert">
                {searchError}
              </span>
            ) : null}
          </form>
        </div>
        <div className="topbar-meta">
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${nextTheme} theme`}
            title={`Switch to ${nextTheme} theme`}
          >
            {theme === 'dark' ? (
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="3.25" />
                <path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2M4 4l1.4 1.4M14.6 14.6 16 16M16 4l-1.4 1.4M5.4 14.6 4 16" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="M16.7 12.2A7 7 0 0 1 7.8 3.3a7 7 0 1 0 8.9 8.9Z" />
              </svg>
            )}
          </button>
          {!authLoading && user ? (
            <div className="account-menu" ref={accountRef}>
              <button
                type="button"
                className="account-trigger"
                onClick={() => setAccountOpen((open) => !open)}
                aria-label={`Open account menu for ${user.name ?? user.email}`}
                aria-expanded={accountOpen}
                aria-haspopup="menu"
              >
                <span className="account-avatar" aria-hidden="true">
                  {accountInitials(user.name, user.email)}
                </span>
                <span className="account-trigger-name">{user.name ?? user.email}</span>
                <span className="account-chevron" aria-hidden="true">
                  ▾
                </span>
              </button>
              {accountOpen ? (
                <div className="account-popover" role="menu">
                  <div className="account-identity">
                    <strong>{user.name ?? user.email}</strong>
                    <span>{user.email}</span>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAccountOpen(false);
                      void signOut();
                    }}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>
      <aside className="sidebar" aria-hidden={!sidebarOpen}>
        <nav aria-label="Primary navigation">
          <NavLink to="/" end>
            Dashboard
          </NavLink>
          <NavLink
            to="/inventory"
            end
            className={({ isActive }) => (isActive && !isExceptionRoute ? 'active' : undefined)}
          >
            <span>Inventory</span>
            {items.data ? (
              <span className="nav-count">{summary.total.toLocaleString()}</span>
            ) : null}
          </NavLink>
          <NavLink to="/inventory?attention=true" className={isExceptionRoute ? 'active' : ''}>
            <span>Exceptions</span>
            {items.data ? (
              <span className="nav-count nav-count-exception">
                {summary.needsAttention.toLocaleString()}
              </span>
            ) : null}
          </NavLink>
          <NavLink to="/parts">Parts</NavLink>
          <NavLink to="/customers">Customers</NavLink>
          <NavLink to="/fulfillment">Pick &amp; pack</NavLink>
          <NavLink to="/stock">Stock</NavLink>
          <NavLink to="/goods-intake">Goods intake</NavLink>
          <NavLink to="/shipments">Shipments</NavLink>
          <NavLink to="/field-work">Field work</NavLink>
          <NavLink to="/notifications">Notifications</NavLink>
          <NavLink to="/help">Help & guides</NavLink>
          {/* Sites and Audit hidden: both depend on real-backend endpoints
              (global units listing, global audit history) that don't exist
              server-side yet. See tidaltech/koi#7722 and the Audit history gap.
              Customers above is a separate, working route: it's built entirely
              from customer-scoped calls (ListCustomers, GetCustomerSubscription,
              and ListSites/ListItems filtered by one customerId), which the real
              backend already supports — see
              docs/decisions/ADR-0007-customer-entitlements-workspace.md. */}
        </nav>
      </aside>
      {sidebarOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}
      <main id="main-content" className="content" tabIndex={-1}>
        <div className="page-stage" key={location.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
